// AssoDashboard.jsx — Vue d'ensemble éditoriale (staff)
// Conserve la logique métier intacte ; refonte visuelle uniquement
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Users, Calendar, Handshake, Euro, ArrowUpRight, Loader2 } from 'lucide-react';
import { useOutletContext, Link } from 'react-router-dom';

export default function AssoDashboard() {
  const { asso } = useOutletContext();
  const [stats, setStats] = useState({ membres: null, sponsors: null, evenements: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [m, s, e] = await Promise.all([
          api.get('/membres/stats'),
          api.get('/sponsors/stats'),
          api.get('/evenements'),
        ]);
        setStats({ membres: m.data, sponsors: s.data, evenements: e.data });
      } catch {} finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return (
      <div className="p-8 grid place-items-center min-h-[60vh]">
        <Loader2 className="w-6 h-6 animate-spin text-[#94A3B8]" />
      </div>
    );
  }

  const upcoming = stats.evenements.filter((e) => new Date(e.date_debut) > new Date()).slice(0, 3);

  return (
    <div className="px-4 sm:px-8 lg:px-12 py-8 lg:py-12 max-w-7xl mx-auto" data-testid="asso-dashboard">
      {/* En-tête éditorial */}
      <div className="mb-10 slide-up">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] mb-4" style={{ color: 'var(--theme-primary)' }}>
          Vue d'ensemble
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-[#0F172A] leading-[1.02] mb-3">
          Bienvenue <span className="italic font-medium" style={{ color: 'var(--theme-primary)' }}>{asso?.nom ? `· ${asso.nom}` : '.'}</span>
        </h1>
        <p className="text-base sm:text-lg text-[#475569] max-w-2xl">
          Pilotez votre association en un coup d'œil — membres, événements, comptabilité, agents IA.
        </p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12 stagger" data-testid="asso-kpis">
        <KPI icon={Users} label="Membres actifs" value={stats.membres?.actifs || 0} sub={`/ ${stats.membres?.total || 0} total`} testId="kpi-membres" />
        <KPI icon={Euro} label="Cotisations" value={`${Number(stats.membres?.revenus || 0).toFixed(0)}€`} sub="cumulées" testId="kpi-cotisations" />
        <KPI icon={Handshake} label="Sponsors actifs" value={stats.sponsors?.actifs || 0} sub={`${Number(stats.sponsors?.revenus_actifs || 0).toFixed(0)}€ engagés`} testId="kpi-sponsors" />
        <KPI icon={Calendar} label="Événements" value={upcoming.length} sub="à venir" testId="kpi-evenements" />
      </div>

      {/* Section principale 2 colonnes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Événements à venir */}
        <section className="bg-white border border-[#E2E8F0] rounded-3xl p-7">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display text-xl font-bold text-[#0F172A] leading-tight">Prochains événements</h2>
            <Link
              to="/dashboard/evenements"
              className="text-xs font-bold text-[#475569] hover:text-[#0F172A] flex items-center gap-1 transition-colors"
            >
              Voir tout <ArrowUpRight className="w-3 h-3" />
            </Link>
          </div>
          {upcoming.length === 0 ? (
            <div className="text-center py-10">
              <div className="w-12 h-12 rounded-2xl bg-[#F1F5F9] grid place-items-center mx-auto mb-3">
                <Calendar className="w-5 h-5 text-[#94A3B8]" />
              </div>
              <p className="text-sm text-[#475569]">Aucun événement à venir.</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="asso-upcoming-events">
              {upcoming.map((e) => {
                const date = new Date(e.date_debut);
                return (
                  <div key={e.id} className="flex items-center gap-4 p-3 rounded-xl hover:bg-[#F9F9F8] transition-colors">
                    <div className="w-12 h-12 rounded-2xl grid place-items-center shrink-0" style={{ background: 'var(--theme-primary-soft)', color: 'var(--theme-primary)' }}>
                      <div className="text-center leading-none">
                        <div className="text-[9px] uppercase tracking-wider font-bold">{date.toLocaleDateString('fr-FR', { month: 'short' })}</div>
                        <div className="font-display text-lg font-black mt-0.5">{date.getDate()}</div>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-[#0F172A] truncate">{e.nom}</div>
                      <div className="text-xs text-[#475569] mt-0.5">
                        {date.toLocaleString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                        {e.lieu && ` · ${e.lieu}`}
                      </div>
                    </div>
                    {e.billetterie && <span className="badge badge-primary">{e.nb_billets_vendus} billets</span>}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Carte d'identité asso */}
        <section className="bg-white border border-[#E2E8F0] rounded-3xl p-7">
          <h2 className="font-display text-xl font-bold text-[#0F172A] leading-tight mb-6">Votre association</h2>
          <div className="space-y-0">
            <Row label="Nom" value={asso?.nom} />
            <Row label="Plan" value={<span className="badge badge-primary uppercase">{asso?.plan_id} · {Number(asso?.prix_mensuel || 0).toFixed(0)}€/mois</span>} />
            <Row label="Statut" value={<span className={`badge ${asso?.abonnement_statut === 'active' ? 'badge-success' : 'badge-info'}`}>{asso?.abonnement_statut}</span>} />
            <Row label="Type" value={<span className="capitalize">{asso?.type}</span>} />
            <Row label="Ville" value={asso?.ville || '—'} />
            <Row
              label="Couleur"
              value={
                <span className="inline-flex items-center gap-2">
                  <span className="w-4 h-4 rounded-md ring-1 ring-[#E2E8F0]" style={{ background: asso?.couleur_principale }} />
                  <span className="font-mono text-xs">{asso?.couleur_principale}</span>
                </span>
              }
              last
            />
          </div>
        </section>
      </div>
    </div>
  );
}

function KPI({ icon: Icon, label, value, sub, testId }) {
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-3xl p-5 sm:p-6 transition-all hover:shadow-md hover:-translate-y-0.5" data-testid={testId}>
      <div className="flex items-center justify-between mb-4">
        <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#475569]">{label}</div>
        <div className="w-8 h-8 rounded-lg grid place-items-center" style={{ background: 'var(--theme-primary-soft)', color: 'var(--theme-primary)' }}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div className="font-display text-3xl sm:text-4xl font-black tracking-tight text-[#0F172A]">{value}</div>
      <div className="text-xs text-[#94A3B8] mt-1">{sub}</div>
    </div>
  );
}

function Row({ label, value, last = false }) {
  return (
    <div className={`flex items-center justify-between py-3 ${last ? '' : 'border-b border-[#F1F5F9]'}`}>
      <span className="text-sm text-[#475569]">{label}</span>
      <span className="font-semibold text-[#0F172A] text-right text-sm">{value}</span>
    </div>
  );
}
